<template>
  <v-dialog
    v-model="dialog"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn color="primary" large v-bind="attrs" v-on="on">
        <v-icon>mdi-plus</v-icon>
        Add New Star
      </v-btn>
    </template>
    <v-card class="text-start">
      <v-toolbar dark color="primary">
        <v-btn icon dark @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>Add New Star</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn dark text @click="dialog = false">Save</v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-row no-gutters justify="space-between">
        <v-col md="6">
          <v-subheader>Variant of the star</v-subheader>
          <v-list-item-group multiple active-class="">
            <v-list-item
              @click="variant.singleVariant = !variant.singleVariant"
            >
              <template>
                <v-list-item-action @click.stop="">
                  <v-checkbox
                    :disabled="variant.double || variant.super"
                    v-model="variant.singleVariant"
                  ></v-checkbox>
                </v-list-item-action>

                <v-list-item-content>
                  <v-list-item-title>Single Star</v-list-item-title>
                  <v-list-item-subtitle>
                    A visible star, anywhere in the sky.
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-list-item>
            <v-list-item
              @click="variant.binaryVariant = !variant.binaryVariant"
            >
              <template>
                <v-list-item-action @click.stop="">
                  <v-checkbox v-model="variant.binaryVariant"></v-checkbox>
                </v-list-item-action>

                <v-list-item-content>
                  <v-list-item-title>Binary Star</v-list-item-title>
                  <v-list-item-subtitle>
                    Two rare orbiting stars.
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-list-item>
            <v-list-item
              @click="variant.specialVariant = !variant.specialVariant"
            >
              <template>
                <v-list-item-action @click.stop="">
                  <v-checkbox v-model="variant.specialVariant"></v-checkbox>
                </v-list-item-action>

                <v-list-item-content>
                  <v-list-item-title>Constellation Star</v-list-item-title>
                  <v-list-item-subtitle>
                    A star from a constellation of zodiac. Easy to locate in the
                    night sky.
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-list-item>
          </v-list-item-group>

          <v-divider class="my-2"></v-divider>
          <v-subheader>Brightness of the star</v-subheader>
          <v-list-item-group active-class="">
            <v-radio-group v-model="brightness">
              <v-list-item @click="brightness = 'visible'">
                <template>
                  <v-list-item-action @click.stop="">
                    <v-radio value="visible"></v-radio>
                  </v-list-item-action>

                  <v-list-item-content>
                    <v-list-item-title>Visible Star</v-list-item-title>
                    <v-list-item-subtitle>
                      A visible star, with magnitude higher than 4
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-list-item>
              <v-list-item @click="brightness = 'bright'">
                <template>
                  <v-list-item-action @click.stop="">
                    <v-radio value="bright"></v-radio>
                  </v-list-item-action>

                  <v-list-item-content>
                    <v-list-item-title>Bright Star</v-list-item-title>
                    <v-list-item-subtitle>
                      Easy to locate in the night sky. Magnitude of the star is
                      ranging between 4 and 1.7
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-list-item>
              <v-list-item @click="brightness = 'brighter'">
                <template>
                  <v-list-item-action @click.stop="">
                    <v-radio value="brighter"></v-radio>
                  </v-list-item-action>

                  <v-list-item-content>
                    <v-list-item-title>Very Bright Star</v-list-item-title>
                    <v-list-item-subtitle>
                      Easier to locate in the night sky. Magnitude of the star
                      is less than 1.7
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-list-item>
            </v-radio-group>
          </v-list-item-group>

          <v-divider class="my-2"></v-divider>

          <v-container>
            <v-text-field
              v-model="starName"
              hide-details
              required
              outlined
              clearable
              class="mb-2"
            >
              <template #label>
                Name of the Star
                <span class="red--text"><strong>*</strong></span>
              </template>
            </v-text-field>
            <v-row>
              <v-col>
                <v-text-field v-model="coords" hide-details outlined clearable>
                  <template #label>
                    {{ 'Coordinates' + (variant.binaryVariant ? '_1' : '') }}
                    <span class="red--text"><strong>*</strong></span>
                  </template>
                </v-text-field>
              </v-col>
              <v-col v-if="variant.binaryVariant">
                <v-text-field
                  v-model="coords_2"
                  hide-details
                  outlined
                  clearable
                >
                  <template #label>
                    Coordinates_2
                    <span class="red--text"><strong>*</strong></span>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-col>
        <v-col md="6">
          <v-row no-gutters justify="space-between" align="center">
            <v-col>
              <v-text-field
                v-model="searchName"
                ref="searchName"
                label="Search Sky Source (Noctua Database)"
                prepend-icon="mdi-star-circle-outline"
              ></v-text-field>
            </v-col>
            <v-col cols="1">
              <v-btn
                fab
                x-small
                color="primary"
                @click="searchSkySource(searchName)"
              >
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <v-textarea
            auto-grow
            v-model="message"
            label="SkySource (Noctua Database)"
            prepend-icon="mdi-star-settings-outline"
            rows="3"
            hint="(Optional) This message will be displayed on info panel"
            aria-required="true"
          ></v-textarea>
        </v-col>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
  import { searchSkySources } from '../services/skySourceService'

  export default {
    data() {
      return {
        dialog: false,
        notifications: false,
        sound: true,
        widgets: false,
        searchName: '',
        message: '',
        variant: {
          singleVariant: false,
          binaryVariant: false,
          specialVariant: false
        },
        coords: '',
        coords_2: '',
        brightness: '',
        starName: ''
      }
    },

    methods: {
      async searchSkySource(name) {
        console.log(this.variant)
        if (!name) {
          this.$refs.searchName.focus()
          return
        }

        try {
          const { data } = await searchSkySources(name.toUpperCase())
          this.message = JSON.stringify(data, null, 4)
          this.starName = data.short_name

          this.brightness = this.getBrightness(data.model_data)
          this.variant = this.getVariants(data.types)

          const starObj = {
            name: this.starName,
            magnitude: this.model_data.Vmag,
            spectral: data.model_data.spect_t,
            radec: this.coords,
            coords: '03 48 51.6   +24 10 36.0',
            variant: this.variant,
            brightness: this.brightness
          }
          console.log(starObj)
        } catch (error) {
          this.showSnackbar('red', 'Star Not Found!')
        }
      },

      formatCoords() {},

      getBrightness({ Vmag: mag }) {
        if (mag > 4) return 'visible'
        else if (mag <= 4 && mag >= 1.7) return 'bright'
        else return 'brighter'
      },

      getVariants(types) {
        return {
          singleVariant: types.includes('*'),
          binaryVariant: types.includes('**'),
          specialVariant: false
        }
      },

      formatDistance: function (d) {
        const astroConstants = {
          // Light time for 1 au in s
          ERFA_AULT: 499.004782,
          // Seconds per day
          ERFA_DAYSEC: 86400.0,
          // Days per Julian year
          ERFA_DJY: 365.25,
          // Astronomical unit in m
          ERFA_DAU: 149597870000
        }
        // d is in AU
        if (!d) {
          return 'NAN'
        }
        const ly =
          (d * astroConstants.ERFA_AULT) /
          astroConstants.ERFA_DAYSEC /
          astroConstants.ERFA_DJY
        if (ly >= 0.1) {
          return ly.toFixed(2) + ' light years'
        }
        if (d >= 0.1) {
          return d.toFixed(2) + ' AU'
        }
        const meter = d * astroConstants.ERFA_DAU
        if (meter >= 1000) {
          return (meter / 1000).toFixed(2) + ' km'
        }
        return meter.toFixed(2) + ' m'
      }
    },
    watch: {
      brightness(val) {
        console.log(val)
      }
    }
  }
</script>
